// ================================================
// CLARITY REPORTING — PAYMENT PAGE
// Credit card (Authorize.net) + PayPal choice
// ================================================

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const PLANS = {
  site_owner:  { name: 'Site Owner',  price: '$20/mo',  amount: 20 },
  agency:      { name: 'Agency',      price: '$99/mo',  amount: 99 },
  agency_pro:  { name: 'Agency Pro',  price: '$199/mo', amount: 199 }
};

// Format card number with spaces: 4111 1111 1111 1111
function formatCardNumber(val) {
  return val.replace(/\D/g, '').slice(0, 16).replace(/(.{4})/g, '$1 ').trim();
}

// Format expiry as MM/YY
function formatExpiry(val) {
  const digits = val.replace(/\D/g, '').slice(0, 4);
  if (digits.length >= 3) return `${digits.slice(0,2)}/${digits.slice(2)}`;
  return digits;
}

// Convert MM/YY display to YYYY-MM for Authorize.net
function expiryToYYYYMM(display) {
  const [mm, yy] = display.split('/');
  if (!mm || !yy) return '';
  return `20${yy}-${mm.padStart(2, '0')}`;
}

export default function PaymentPage() {
  const { user, updateUser } = useAuth();
  const navigate             = useNavigate();
  const [searchParams]       = useSearchParams();

  const planType = searchParams.get('plan') || user?.account_type || 'site_owner';
  const plan     = PLANS[planType] || PLANS.site_owner;

  const [method,    setMethod]    = useState('card');  // 'card' | 'paypal'
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState('');

  // Card form state
  const [cardNumber,  setCardNumber]  = useState('');
  const [expiry,      setExpiry]      = useState('');
  const [cvv,         setCvv]         = useState('');
  const [nameOnCard,  setNameOnCard]  = useState(`${user?.first_name || ''} ${user?.last_name || ''}`.trim());

  // Handle PayPal return (user came back from PayPal approval page)
  useEffect(() => {
    const paypalSubId = searchParams.get('subscription_id');
    const baToken     = searchParams.get('ba_token');

    if (paypalSubId) {
      activatePayPal(paypalSubId);
    }
  }, []);

  // ---- Activate PayPal after redirect ----
  async function activatePayPal(subscriptionId) {
    setLoading(true);
    try {
      const { data } = await api.post('/subscriptions/paypal/activate', {
        subscription_id: subscriptionId
      });
      updateUser({ account_type: planType });
      toast.success('Subscription activated!');
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to activate PayPal subscription. Please contact support.');
    } finally {
      setLoading(false);
    }
  }

  // ---- Submit credit card payment ----
  async function handleCardSubmit(e) {
    e.preventDefault();
    setError('');

    const rawCard = cardNumber.replace(/\s/g, '');
    if (rawCard.length < 15) {
      setError('Please enter a valid card number.');
      return;
    }

    const expirationDate = expiryToYYYYMM(expiry);
    if (!expirationDate) {
      setError('Please enter a valid expiry date (MM/YY).');
      return;
    }

    setLoading(true);
    try {
      await api.post('/subscriptions/authorize-net', {
        plan_type:      planType,
        cardNumber:     rawCard,
        expirationDate,
        cardCode:       cvv
      });
      updateUser({ account_type: planType });
      toast.success('Subscription activated! Welcome to Clarity Reporting.');
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || 'Payment failed. Please check your card details and try again.');
    } finally {
      setLoading(false);
    }
  }

  // ---- Initiate PayPal flow ----
  async function handlePayPal() {
    setError('');
    setLoading(true);
    try {
      const { data } = await api.post('/subscriptions/paypal', { plan_type: planType });
      // Redirect user to PayPal to approve the subscription
      window.location.href = data.approvalUrl;
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to connect to PayPal. Please try again.');
      setLoading(false);
    }
  }

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center',
      justifyContent: 'center', background: '#fafafa',
      fontFamily: 'IBM Plex Sans, sans-serif', padding: 24
    }}>
      <div style={{ width: '100%', maxWidth: 500 }}>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <Link to="/" style={{
            fontSize: 12, fontWeight: 700, letterSpacing: 3,
            textTransform: 'uppercase', textDecoration: 'none', color: '#1a1a1a',
            display: 'block', marginBottom: 24
          }}>
            Clarity Reporting
          </Link>
          <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 8 }}>
            Complete your subscription
          </h1>
          <p style={{ fontSize: 13, color: '#888' }}>
            You're subscribing to the <strong>{plan.name}</strong> plan at <strong>{plan.price}</strong>.
            Cancel any time.
          </p>
        </div>

        {/* Plan summary box */}
        <div style={{
          background: '#1a1a1a', color: '#fff',
          padding: '16px 20px', marginBottom: 24,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center'
        }}>
          <div>
            <div style={{ fontSize: 12, letterSpacing: 2, textTransform: 'uppercase', opacity: 0.5, marginBottom: 4 }}>
              Selected Plan
            </div>
            <div style={{ fontWeight: 700, fontSize: 15 }}>{plan.name}</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 24, fontWeight: 700 }}>{plan.price}</div>
          </div>
        </div>

        {/* Error message */}
        {error && (
          <div style={{
            background: '#fff0f0', borderLeft: '4px solid #CC0000',
            color: '#CC0000', padding: '12px 16px', fontSize: 13, marginBottom: 20
          }}>
            {error}
          </div>
        )}

        {/* Payment method tabs */}
        <div style={{
          display: 'grid', gridTemplateColumns: '1fr 1fr',
          border: '1px solid #d0d0d0', marginBottom: 24
        }}>
          <button
            onClick={() => setMethod('card')}
            style={{
              padding: '12px', border: 'none', cursor: 'pointer',
              background: method === 'card' ? '#1a1a1a' : '#fff',
              color:      method === 'card' ? '#fff' : '#888',
              fontSize: 12, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase',
              borderRight: '1px solid #d0d0d0', transition: 'all 0.15s'
            }}
          >
            💳 Credit Card
          </button>
          <button
            onClick={() => setMethod('paypal')}
            style={{
              padding: '12px', border: 'none', cursor: 'pointer',
              background: method === 'paypal' ? '#1a1a1a' : '#fff',
              color:      method === 'paypal' ? '#fff' : '#888',
              fontSize: 12, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase',
              transition: 'all 0.15s'
            }}
          >
            PayPal
          </button>
        </div>

        {/* ---- CREDIT CARD FORM ---- */}
        {method === 'card' && (
          <form onSubmit={handleCardSubmit}>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', color: '#666', marginBottom: 8 }}>
                Name on Card
              </label>
              <input
                style={inputStyle}
                placeholder="Full Name"
                value={nameOnCard}
                onChange={e => setNameOnCard(e.target.value)}
                required
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Card Number</label>
              <input
                style={{ ...inputStyle, fontFamily: 'IBM Plex Mono, monospace', letterSpacing: 2 }}
                placeholder="4111 1111 1111 1111"
                value={cardNumber}
                onChange={e => setCardNumber(formatCardNumber(e.target.value))}
                inputMode="numeric"
                required
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 24 }}>
              <div>
                <label style={labelStyle}>Expiry Date</label>
                <input
                  style={{ ...inputStyle, fontFamily: 'IBM Plex Mono, monospace' }}
                  placeholder="MM/YY"
                  value={expiry}
                  onChange={e => setExpiry(formatExpiry(e.target.value))}
                  inputMode="numeric"
                  required
                />
              </div>
              <div>
                <label style={labelStyle}>CVV</label>
                <input
                  style={{ ...inputStyle, fontFamily: 'IBM Plex Mono, monospace' }}
                  placeholder="123"
                  value={cvv}
                  onChange={e => setCvv(e.target.value.replace(/\D/g,'').slice(0, 4))}
                  inputMode="numeric"
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%', padding: '14px',
                background: loading ? '#888' : '#1a1a1a',
                color: '#fff', border: 'none', cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: 13, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase'
              }}
            >
              {loading ? 'Processing...' : `Subscribe — ${plan.price}`}
            </button>

            <p style={{ fontSize: 11, color: '#aaa', textAlign: 'center', marginTop: 12, lineHeight: 1.6 }}>
              Your card will be charged {plan.price} today and on the same date each month.
              Payments processed securely by Authorize.net. Your full card number is never stored.
            </p>
          </form>
        )}

        {/* ---- PAYPAL ---- */}
        {method === 'paypal' && (
          <div style={{ textAlign: 'center' }}>
            <p style={{ fontSize: 14, color: '#555', lineHeight: 1.7, marginBottom: 28 }}>
              You'll be redirected to PayPal to authorize the subscription.
              Once approved, you'll return here and your account will be activated immediately.
            </p>

            <button
              onClick={handlePayPal}
              disabled={loading}
              style={{
                width: '100%', padding: '14px',
                background: loading ? '#aaa' : '#FFB800',
                color: '#1a1a1a', border: 'none',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: 14, fontWeight: 700, letterSpacing: 0.5,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                marginBottom: 16
              }}
            >
              {loading ? (
                'Connecting to PayPal...'
              ) : (
                <>
                  <PayPalLogo />
                  Pay with PayPal
                </>
              )}
            </button>

            <p style={{ fontSize: 11, color: '#aaa', lineHeight: 1.6 }}>
              You'll be billed {plan.price} today and on the same date each month.
              Cancel any time from your account settings.
            </p>
          </div>
        )}

        {/* Back link */}
        <div style={{ textAlign: 'center', marginTop: 24 }}>
          <Link to="/register" style={{ fontSize: 12, color: '#aaa', textDecoration: 'none' }}>
            ← Back to registration
          </Link>
        </div>

      </div>
    </div>
  );
}

// ---- Inline styles ----
const labelStyle = {
  display: 'block', fontSize: 11, fontWeight: 700,
  letterSpacing: 1.5, textTransform: 'uppercase',
  color: '#666', marginBottom: 8
};

const inputStyle = {
  width: '100%', padding: '10px 14px',
  fontSize: 14, border: '1px solid #d0d0d0',
  outline: 'none', boxSizing: 'border-box',
  borderRadius: 0, fontFamily: 'IBM Plex Sans, sans-serif'
};

// Simple PayPal wordmark SVG
function PayPalLogo() {
  return (
    <svg height="20" viewBox="0 0 101 32" xmlns="http://www.w3.org/2000/svg">
      <path fill="#003087" d="M12.237 2.8H5.895C5.478 2.8 5.1 3.1 5.037 3.513L2.3 20.814c-.044.298.186.567.488.567h3.164c.417 0 .795-.3.858-.713l.638-4.048c.063-.413.441-.712.858-.712h2.064c4.32 0 6.814-2.09 7.467-6.23.294-1.814.012-3.24-.835-4.239C15.557 3.34 14.118 2.8 12.237 2.8z"/>
      <path fill="#003087" d="M37.932 2.8h-6.341c-.417 0-.795.3-.858.713L28 20.814c-.043.298.186.567.488.567h3.387c.291 0 .539-.212.584-.5l.679-4.307c.063-.413.44-.712.857-.712h2.065c4.32 0 6.813-2.09 7.466-6.23.294-1.814.013-3.24-.834-4.239C41.252 3.34 39.813 2.8 37.932 2.8z"/>
      <path fill="#009cde" d="M23.913 8.669c-.395-2.311-2.379-3.87-4.944-3.87h-5.23c-.352 0-.653.256-.708.604L10.82 20.81c-.042.286.18.543.47.543h3.088l.774-4.91-.024.153c.055-.348.353-.604.706-.604h1.47c2.894 0 5.16-1.176 5.824-4.577.02-.1.037-.196.051-.29z"/>
    </svg>
  );
}
